import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getAttendanceCalendar } from './api/studentScreensApi'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function computeMonthStats(year, month, days) {
  const totalDays = getDaysInMonth(year, month)
  let present = 0
  let absent = 0

  for (let d = 1; d <= totalDays; d++) {
    const status = days[d]
    if (status === 'P') present++
    else if (status === 'A') absent++
  }

  const total = present + absent
  const pct = total > 0 ? Math.round((present / total) * 100) : 0

  return { present, absent, pct, totalDays }
}

export default function StudentAttendanceScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')

  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [months, setMonths] = useState([])

  useEffect(() => {
    loadStudent()
    loadAttendance()
  }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({
        id: studentId,
        firstName: 'Student',
        lastName: '',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const loadAttendance = async () => {
    const now = new Date()
    const results = await Promise.all(
      Array.from({ length: 12 }, async (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const year = d.getFullYear()
        const month = d.getMonth()
        const key = `${year}-${String(month + 1).padStart(2, '0')}`
        let days = {}
        let present = 0
        let absent = 0
        try {
          const cal = await getAttendanceCalendar(studentId, year, month + 1)
          days = cal?.days || {}
          present = cal?.present || 0
          absent = cal?.absent || 0
        } catch {
          // keep empty calendar for months without data
        }
        const stats = computeMonthStats(year, month, days)
        return {
          key,
          year,
          month,
          label: `${MONTH_NAMES[month]} ${year}`,
          days,
          stats: { ...stats, present, absent },
        }
      })
    )
    setMonths(results)
  }

  const grandTotal = useMemo(() => {
    let present = 0
    let absent = 0
    months.forEach(({ stats }) => {
      present += stats.present
      absent += stats.absent
    })
    const total = present + absent
    return { present, absent, pct: total > 0 ? Math.round((present / total) * 100) : 0 }
  }, [months])

  const goBack = () => navigate('/admission/students/list')
  const studentName = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1480px] items-center justify-center rounded-[2rem] bg-surface-container-low px-6 py-20 text-sm text-on-surface-variant">
        Loading attendance data...
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Attendance</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">
            Attendance of {studentName}
          </h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to Students
        </button>
      </section>

      <div className="flex items-center gap-3 rounded-2xl bg-primary px-6 py-4">
        <span className="material-symbols-outlined text-on-primary text-[22px]">fact_check</span>
        <h2 className="text-lg font-bold text-on-primary">
          Attendance of {studentName}
        </h2>
      </div>

      <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
        <div className="overflow-x-auto">
          <table className="w-full text-center">
            <thead>
              <tr className="border-b border-outline-variant/30 bg-surface-container-low">
                <th className="sticky left-0 z-10 bg-surface-container-low px-4 py-3 text-xs font-black uppercase tracking-wider text-on-surface-variant min-w-[140px] text-left">
                  Month
                </th>
                {Array.from({ length: 31 }, (_, i) => (
                  <th key={i} className="px-1 py-3 text-[10px] font-bold text-on-surface-variant min-w-[28px]">
                    {i + 1}
                  </th>
                ))}
                <th className="px-3 py-3 text-xs font-black uppercase tracking-wider text-on-surface-variant min-w-[60px]">
                  Present
                </th>
                <th className="px-3 py-3 text-xs font-black uppercase tracking-wider text-on-surface-variant min-w-[60px]">
                  Absent
                </th>
                <th className="px-3 py-3 text-xs font-black uppercase tracking-wider text-on-surface-variant min-w-[50px]">
                  %
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {months.map((row, idx) => (
                <tr
                  key={row.key}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                >
                  <td className="sticky left-0 z-10 px-4 py-3 text-left text-sm font-bold text-on-surface whitespace-nowrap" style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    {row.label}
                  </td>
                  {Array.from({ length: 31 }, (_, i) => {
                    const day = i + 1
                    const status = row.days[day]
                    const hasDay = day <= row.stats.totalDays

                    if (!hasDay) {
                      return <td key={day} className="px-1 py-2 text-xs text-outline-variant/40">—</td>
                    }

                    if (!status) {
                      return (
                        <td key={day} className="px-1 py-2 text-xs text-on-surface-variant/50">
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-surface-variant/30 text-[9px]">—</span>
                        </td>
                      )
                    }

                    return (
                      <td key={day} className="px-1 py-2">
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-black ${
                            status === 'P'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                    )
                  })}
                  <td className="px-3 py-3">
                    <span className="inline-flex min-w-[24px] justify-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-black text-green-700">
                      {row.stats.present}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="inline-flex min-w-[24px] justify-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-black text-red-700">
                      {row.stats.absent}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-sm font-black ${
                      row.stats.pct >= 85 ? 'text-green-700' : row.stats.pct >= 70 ? 'text-amber-700' : 'text-red-700'
                    }`}>
                      {row.stats.pct}%
                    </span>
                  </td>
                </tr>
              ))}

              <tr className="border-t-2 border-outline-variant/30 bg-surface-container-low font-bold">
                <td className="sticky left-0 z-10 bg-surface-container-low px-4 py-3 text-left text-sm text-on-surface">
                  Total
                </td>
                {Array.from({ length: 31 }, (_, i) => (
                  <td key={i} className="px-1 py-3" />
                ))}
                <td className="px-3 py-3">
                  <span className="inline-flex min-w-[24px] justify-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-black text-green-800">
                    {grandTotal.present}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className="inline-flex min-w-[24px] justify-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-black text-red-800">
                    {grandTotal.absent}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-sm font-black ${
                    grandTotal.pct >= 85 ? 'text-green-700' : grandTotal.pct >= 70 ? 'text-amber-700' : 'text-red-700'
                  }`}>
                    {grandTotal.pct}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>
              Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.createdDate}</span>
            </span>
          )}
          {student?.updatedBy && (
            <span>
              Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on{' '}
              <span className="font-semibold text-on-surface">{student.updatedDate}</span>
            </span>
          )}
        </div>
      </section>
    </div>
  )
}
